import React, { createRef, useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useAntdTable, useRequest } from 'ahooks';
import { Table, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import { useSelector } from 'react-redux';
import { ConBrandDetailsWebController } from '@/services/swagger/ConBrandDetailsWebController';
import type { ConCompanyPatent } from '@/services/swagger/data-contracts';
import { RootState } from '@/models';
import TooltipBase from '@/components/tooltip';
import ChartHeader from '../chart-header';
import Chart from '../chart';
import InfoCard from '../InfoCard';
import Ring from '../Ring';
import LockedCard, { LockCardState } from '../LockedCard';
import styles from './index.module.scss';

const brandDetailController = new ConBrandDetailsWebController();

export type ProductCompetitivenessProps = {
  /** 品牌id */
  conBrandId: string;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 品牌产品数量 */
  productNum?: number;
  /** 品牌解锁状态改变回调 */
  onLockedCardStateChange?: (state: string) => void;
};

const controller = new ConBrandDetailsWebController();

const ProductCompetitiveness: React.FC<ProductCompetitivenessProps> = ({
  conBrandId,
  brandLockState = 'not_member',
  productNum,
  onLockedCardStateChange,
}) => {
  const { token } = useSelector((state: RootState) => state.user);
  /** 产品竞争能力：专利信息、热门单品数量占比、热门单品销售额占比 */
  const { data: productInfo = {} } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1BrandProductInfoUsingGet1({
        conBrandId,
      });
      return res.data.data;
    },
    { refreshDeps: [brandLockState] }
  );

  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );
  const [dataSource, setDataSource] = useState<
    TableProps<
      ConCompanyPatent & {
        patentNameRef?: RefObject<HTMLDivElement>;
        applyNumberRef?: RefObject<HTMLDivElement>;
        publicNumberRef?: RefObject<HTMLDivElement>;
      }
    >['dataSource']
  >([]);
  const [renderToolTip, setRenderTooltip] = useState<
    { patentName?: boolean; applyNumber?: boolean; publicNumber?: boolean }[]
  >([]);
  /** 专利信息 */
  const { tableProps } = useAntdTable(
    async ({ current, pageSize }) => {
      const res = await controller.conBrandDetailsWebControllerV1CompanyPatentUsingGet1({
        conBrandId,
        pageIndex: current,
        pageSize,
      });
      const list =
        res.data.data.companyPatentList?.map(item => {
          return {
            patentNameRef: createRef<HTMLDivElement>(),
            applyNumberRef: createRef<HTMLDivElement>(),
            publicNumberRef: createRef<HTMLDivElement>(),
            ...item,
          };
        }) || [];
      setDataSource(list);
      return {
        list,
        total: res.data.data.total,
      };
    },
    {
      defaultPageSize: 5,
    }
  );
  const columns: TableProps<
    ConCompanyPatent & {
      patentNameRef: RefObject<HTMLDivElement>;
      applyNumberRef: RefObject<HTMLDivElement>;
      publicNumberRef: RefObject<HTMLDivElement>;
    }
  >['columns'] = [
    {
      title: '序号',
      align: 'center',
      width: 60,
      render(_, __, index) {
        const { current, pageSize } = tableProps.pagination;
        return (current - 1) * pageSize + index + 1;
      },
    },
    { title: '申请日', width: 120, dataIndex: 'applyDate', align: 'center' },
    {
      title: '专利名称',
      dataIndex: 'patentName',
      width: 80,
      render(value, record, index) {
        return renderToolTip[index]?.patentName ? (
          <Tooltip title={value} className={styles.ellipsisColumn}>
            <div ref={record.patentNameRef}>{value}</div>
          </Tooltip>
        ) : (
          <div ref={record.patentNameRef} className={styles.ellipsisColumn}>
            {value}
          </div>
        );
      },
    },
    { title: '专利类型', dataIndex: 'patentTypeName', width: 100, align: 'center' },
    {
      title: '专利状态',
      // width: 100,
      dataIndex: 'patentStatusName',
      align: 'center',
      className: styles.patentStatusCell,
    },
    {
      title: '申请号',
      dataIndex: 'applyNumber',
      // width: 80,
      align: 'center',
      // render(value, record, index) {
      //   return renderToolTip[index]?.applyNumber ? (
      //     <Tooltip title={value} className={styles.ellipsisColumn}>
      //       <div ref={record.applyNumberRef}>{value}</div>
      //     </Tooltip>
      //   ) : (
      //     <div ref={record.applyNumberRef} className={styles.ellipsisColumn}>
      //       {value}
      //     </div>
      //   );
      // },
    },
    {
      title: '公开（公布）号',
      dataIndex: 'publicNumber',
      width: 130,
      align: 'center',
      render(value, __, _index): any {
        return value ? value : '-';
      },
      // render(value, record, index) {
      //   return renderToolTip[index]?.publicNumber ? (
      //     <Tooltip title={value} className={styles.ellipsisColumn}>
      //       <div ref={record.publicNumberRef}>{value}</div>
      //     </Tooltip>
      //   ) :
      //     (
      //     <div ref={record.publicNumberRef} className={styles.ellipsisColumn}>
      //       {value}
      //     </div>
      //   );
      // },
    },
    { title: '公开（公告）日', dataIndex: 'openDay', width: 130, align: 'center' },
    // {
    //   title: '发明人',
    //   dataIndex: 'inventorName',
    //   align: 'center',
    //   render(value, record, index) {
    //     return renderToolTip[index]?.publicNumber ? (
    //       <Tooltip title={value} className={styles.ellipsisColumn}>
    //         <div ref={record.publicNumberRef}>{value}</div>
    //       </Tooltip>
    //     ) : (
    //       <div ref={record.publicNumberRef} className={styles.ellipsisColumn}>
    //         {value}
    //       </div>
    //     );
    //   },
    // },
  ];
  useEffect(() => {
    dataSource?.forEach((item, index) => {
      if (
        item.patentNameRef &&
        item.patentNameRef.current &&
        item.patentNameRef?.current?.scrollWidth > item.patentNameRef?.current?.clientWidth
      ) {
        (renderToolTip[index] = renderToolTip[index] || {}).patentName = true;
      }
      if (
        item.applyNumberRef &&
        item.applyNumberRef.current &&
        item.applyNumberRef?.current?.scrollWidth > item.applyNumberRef?.current?.clientWidth
      ) {
        (renderToolTip[index] = renderToolTip[index] || {}).applyNumber = true;
      }
      if (
        item.publicNumberRef &&
        item.publicNumberRef.current &&
        item.publicNumberRef?.current?.scrollWidth > item.publicNumberRef?.current?.clientWidth
      ) {
        (renderToolTip[index] = renderToolTip[index] || {}).publicNumber = true;
      }
    });
    setRenderTooltip(renderToolTip);
  }, [dataSource, renderToolTip]);
  return (
    !!token && (
      <>
        <ChartHeader
          title={
            <h1 className={styles.moduleTitle}>
              产品竞争能力
              <TooltipBase title="由研发创新能力、爆品打造能力等维度搭建的数学模型计算得出。" placement="right" />
            </h1>
          }
          className={styles.productCompetitivenessChartHeader}
        />
        <div className={styles.cardItem}>
          {!!dataSource.length && (
            <Chart title="专利信息" panelClassName={styles.patentInfoChart}>
              <Table<ConCompanyPatent>
                className={styles.table}
                {...tableProps}
                rowKey="conCompanyPatentId"
                columns={columns}
                dataSource={dataSource}
                pagination={{
                  position: ['bottomCenter'],
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  hideOnSinglePage: true,
                }}
              />
            </Chart>
          )}
        </div>
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              产品信息
              <TooltipBase
                title={
                  <>
                    <p>产品数量通过品牌官方旗舰店的产品数量统计得出。</p>
                    <p>数据来源：CBNData数据库</p>
                    <p>热门单品数量占比是指每月品牌单品在行业销售额排名TOP1000单品中的数量占比。</p>
                    <p>数据来源：任拓情报通</p>
                    <p>热门单品销售额占比是指品牌热门单品销售额在品牌整体销售额中的占比。</p>
                    <p>数据来源：任拓情报通</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          panelClassName={styles.productInfoChart}
        >
          <div className={styles.productInfoChartContent}>
            <InfoCard
              className={styles.productNumInfoCard}
              iconXLinkHref="#icon-product_amount"
              title="产品数量"
              info={
                !productNum ? (
                  <span className={styles.emptyStateText}>暂无数据</span>
                ) : (
                  <span className={styles.productNum}>{productNum}</span>
                )
              }
            />
            <InfoCard
              className={styles.hotGoodsInfoCard}
              iconXLinkHref="#icon-storage"
              title="热门单品数量占比"
              info={
                brandLockState !== 'unlocked' ? (
                  <LockedCard
                    conBrandId={conBrandId}
                    className={styles.miniLockedCard}
                    mini
                    backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/Fjdr72C50citghlq8hu7NAe7C_eV.png"
                    state={brandLockState as LockCardState}
                    onStateChange={onStateChange}
                  />
                ) : (
                  <div className={styles.hotGoodsContent}>
                    {!productInfo?.hotRate ? (
                      <span className={styles.emptyStateText}>暂无数据</span>
                    ) : (
                      <>
                        <span className={styles.hotGoodsRate}>{productInfo?.hotRate}%</span>
                        <Ring size="44" rate={productInfo?.hotRate} />
                      </>
                    )}
                  </div>
                )
              }
            />
            <InfoCard
              className={styles.salesInfoCard}
              iconXLinkHref="#icon-sales"
              title="热门单品销售额占比"
              info={
                brandLockState !== 'unlocked' ? (
                  <LockedCard
                    conBrandId={conBrandId}
                    className={styles.miniLockedCard}
                    mini
                    backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/Fjdr72C50citghlq8hu7NAe7C_eV.png"
                    state={brandLockState as LockCardState}
                    onStateChange={onStateChange}
                  />
                ) : (
                  <div className={styles.salesContent}>
                    {!productInfo?.hotSalesVolumeRate ? (
                      <span className={styles.emptyStateText}>暂无数据</span>
                    ) : (
                      <>
                        <span className={styles.salesRate}>{productInfo?.hotSalesVolumeRate}%</span>
                        <Ring size="44" rate={productInfo?.hotSalesVolumeRate} />
                      </>
                    )}
                  </div>
                )
              }
            />
          </div>
        </Chart>
      </>
    )
  );
};

export default ProductCompetitiveness;
